/** @jsxImportSource @emotion/react */
import { Box, Button, Grid, ModalProps, Typography } from "@mui/material";
import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import isEmail from "validator/lib/isEmail";
import { isUser } from "../../../../pages/Folders/components/UploadDocument/functions/isUser";
import LoadingIconButton from "../../buttons/LoadingIconButton";
import CertiblokTextInput from "../../CertiblokTextInput";
import CertiblokIcon from "../../icons/CertiblokIcon";
import MLDialog from "../../poppers";
import UserChip from "../../UserChip";
import UserPickerAutocomplete from "../../UserPickerAutocomplete";
import WhiteModal from "../../WhiteModal";
import { useSendVideoConference } from "../controllers/useSendVideoConference";

type SendVideoConferenceModalProps = Omit<ModalProps, "children"> & {
	toggleOpen: () => void;
};

const SendVideoConferenceModal: React.FC<SendVideoConferenceModalProps> = ({ toggleOpen, ...otherProps }) => {
	const { t } = useTranslation();
	const sendVideoConference = useSendVideoConference();

	useEffect(() => {
		sendVideoConference.setReason("");
		sendVideoConference.resetSelectedContact();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [otherProps.open]);

	return (
		<WhiteModal onClose={toggleOpen} responsive keepMounted={false} {...otherProps}>
			<Fragment>
				<Typography children={t("variousComponents.sendVideoConference")} variant="subtitle1" sx={{ paddingBottom: 6 }} />

				<UserPickerAutocomplete
					label={t("variousComponents.chooseWhoToSendVideoConference")}
					options={sendVideoConference.availableContacts}
					onInputChange={sendVideoConference.onInputChange}
					onSelectedValueChange={sendVideoConference.selectContact}
					autocompleteStatus={sendVideoConference.autocompleteStatus}
					setAutocompleteStatus={sendVideoConference.setAutocompleteStatus}
					invitableUser={sendVideoConference.currentInputValue.length > 0}
					buttonText={sendVideoConference.currentInputValue}
					buttonAction={() => {
						if (isEmail(sendVideoConference.currentInputValue)) {
							sendVideoConference.selectContact(sendVideoConference.currentInputValue);
							return true;
						} else {
							MLDialog.showSnackbar(t("global.invalidEmail"), { variant: "error" });
							return false;
						}
					}}
				/>
				<Grid container spacing={1} sx={{ paddingTop: 2 }}>
					{sendVideoConference.selectedContacts.map((elem, index) => {
						return (
							<Grid item key={isUser(elem) ? elem.id : `invite_${index}`} xs={4} md={3}>
								<UserChip user={elem} deletable onDelete={sendVideoConference.unselectContact} />
							</Grid>
						);
					})}
				</Grid>
				<CertiblokTextInput
					label={t("askFileUpdate.insertNoteToRequest")}
					color="secondary"
					placeholder={t("variousComponents.videoConferenceExample")}
					fullWidth
					containerCss={{ paddingTop: 12 }}
					value={sendVideoConference.reason}
					multiline
					onChange={(ev) => sendVideoConference.setReason(ev.target.value)}
					css={{ height: "auto" }}
					maxRows={10}
				/>
				<Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", paddingTop: 12 }}>
					<Button variant="outlined" color="secondary" onClick={() => toggleOpen()}>
						<CertiblokIcon size={24} color="secondary" name={"close"} />
						{t("global.cancel")}
					</Button>
					<LoadingIconButton
						icon={<CertiblokIcon size={24} color="white" name={"success"} />}
						loading={sendVideoConference.loadingSubmit}
						variant="contained"
						disabled={sendVideoConference.selectedContacts.length === 0 || sendVideoConference.reason.length === 0}
						color="secondary"
						onClick={() => {
							toggleOpen();
							MLDialog.showSnackbar(t("requests.sentRequests"), { variant: "success" });
							sendVideoConference.submit();
						}}
					>
						{t("global.sendRequest")}
					</LoadingIconButton>
				</Box>
			</Fragment>
		</WhiteModal>
	);
};

export default SendVideoConferenceModal;
